import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import { Check } from 'lucide-react';

import { listBillingProviders } from '../../../utils/approvalsApi';

const DIALOG_PAPER_SX = {
  border: '1px solid #000',
  borderRadius: 0,
  boxShadow: '0 4px 4px rgba(0,0,0,0.25)',
};

const TITLE_SX = {
  fontSize: 18,
  fontWeight: 700,
  px: 3,
  py: 2,
  borderBottom: '1px solid #000',
};

const CONTENT_SX = {
  p: 0,
};

const SEARCH_WRAP_SX = {
  p: 2,
  borderBottom: '1px solid rgba(0,0,0,0.12)',
};

const LIST_WRAP_SX = {
  maxHeight: 360,
  overflowY: 'auto',
};

const ROW_BASE_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  px: 2,
  py: 1.25,
  cursor: 'pointer',
  borderBottom: '1px solid rgba(0,0,0,0.06)',
  '&:hover': { bgcolor: '#F8F9FC' },
};

const AVATAR_SX = {
  width: 32,
  height: 32,
  borderRadius: '50%',
  bgcolor: '#000',
  color: '#fff',
  fontWeight: 700,
  fontSize: 13,
  display: 'grid',
  placeItems: 'center',
  flexShrink: 0,
};

const NAME_SX = { fontWeight: 700, fontSize: 14 };
const META_SX = { fontSize: 12, color: '#64748B' };

const LOADING_WRAP_SX = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  py: 6,
};

const EMPTY_SX = {
  textAlign: 'center',
  color: '#64748B',
  fontSize: 13,
  py: 4,
};

const ACTIONS_SX = {
  px: 2,
  py: 1.5,
  borderTop: '1px solid rgba(0,0,0,0.12)',
};

const fullName = (p) => {
  if (!p) return '';
  const first = p.first_name || '';
  const last = p.last_name || '';
  const name = `${first} ${last}`.trim();
  if (name) return name;
  return p.organization_name || p.name || '';
};

const initials = (p) => {
  const f = (p?.first_name || '').trim()[0] || '';
  const l = (p?.last_name || '').trim()[0] || '';
  const both = `${f}${l}`.toUpperCase();
  if (both) return both;
  return (p?.organization_name || '?').trim()[0]?.toUpperCase() || '?';
};

const ProviderRosterPicker = ({ open, onClose, onSelect, currentId }) => {
  const [query, setQuery] = useState('');
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    listBillingProviders()
      .then((res) => {
        if (cancelled) return;
        const data = res?.data;
        const list = Array.isArray(data) ? data : (data?.results || []);
        setProviders(list);
      })
      .catch(() => {
        if (!cancelled) setProviders([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return providers;
    return providers.filter((p) => {
      const name = fullName(p).toLowerCase();
      const npi = (p.npi || '').toLowerCase();
      return name.includes(q) || npi.includes(q);
    });
  }, [providers, query]);

  const handlePick = (p) => {
    onSelect?.(p);
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: DIALOG_PAPER_SX }}
    >
      <DialogTitle sx={TITLE_SX} disableTypography>
        Select Signing Provider
      </DialogTitle>
      <DialogContent sx={CONTENT_SX}>
        <Box sx={SEARCH_WRAP_SX}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search by name or NPI"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
        </Box>
        <Box sx={LIST_WRAP_SX}>
          {loading ? (
            <Box sx={LOADING_WRAP_SX}>
              <CircularProgress size={20} />
            </Box>
          ) : filtered.length === 0 ? (
            <Typography sx={EMPTY_SX}>
              {providers.length === 0
                ? 'No billing providers configured.'
                : 'No providers match your search.'}
            </Typography>
          ) : (
            filtered.map((p) => {
              const selected = p.id === currentId;
              return (
                <Box
                  key={p.id}
                  sx={ROW_BASE_SX}
                  role="button"
                  tabIndex={0}
                  onClick={() => handlePick(p)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handlePick(p);
                    }
                  }}
                >
                  <Box sx={AVATAR_SX}>{initials(p)}</Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={NAME_SX} noWrap>
                      {fullName(p) || '—'}
                    </Typography>
                    <Typography sx={META_SX} noWrap>
                      NPI {p.npi || '—'}
                      {p.taxonomy_code ? ` · ${p.taxonomy_code}` : ''}
                    </Typography>
                  </Box>
                  {selected && <Check size={16} />}
                </Box>
              );
            })
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={ACTIONS_SX}>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProviderRosterPicker;
